import { useState } from 'react';
import useSWR from 'swr';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { User } from '@/lib/hooks/useUser';

const fetcher = (url: string) => fetch(url, { credentials: 'include' }).then((r) => r.json());

interface ScraperStats {
  startedAt: string;
  companiesProcessed: number;
  pscsSearched: number;
  pscsVerified: number;
  websitesFound: number;
  websitesNotFound: number;
  prospectsSaved: number;
  employeesFound: number;
  errors: number;
}

interface PoolInfo {
  total: number;
  available: number;
  waiting: number;
  ips: { label: string; tabs: number; activeTabs?: { query: string; durationSec: number }[] }[];
}

interface LogEntry {
  message: string;
  type: string;
  timestamp: string;
}

interface ScraperStatus {
  status: 'running' | 'stopped' | 'stale' | 'offline';
  updatedAt: string | null;
  stats: ScraperStats | null;
  pool: PoolInfo | null;
  logs: LogEntry[];
}

const statusColors: Record<string, string> = {
  running: 'bg-success-solid',
  stopped: 'bg-danger-solid',
  stale: 'bg-caution-solid',
  offline: 'bg-status-offline',
};

const logTypeColors: Record<string, string> = {
  info: 'text-console-foreground-muted',
  success: 'text-console-success',
  error: 'text-console-error',
  prospect: 'text-console-category',
  search: 'text-console-info',
};

export default function Admin() {
  const { data: user } = useSWR<User>('/api/user', fetcher);
  const { data: status, mutate: refreshStatus } = useSWR<ScraperStatus>(
    '/api/admin/scraper/status',
    fetcher,
    { refreshInterval: 5000 }
  );
  const [isActioning, setIsActioning] = useState(false);

  if (user && !user.isAdmin) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-muted-foreground">Access denied</p>
      </div>
    );
  }

  const handleAction = async (action: 'stop' | 'restart') => {
    setIsActioning(true);
    try {
      await fetch(`/api/admin/scraper/${action}`, { method: 'POST', credentials: 'include' });
      setTimeout(() => refreshStatus(), 2000);
    } catch {}
    setIsActioning(false);
  };

  const stats = status?.stats;
  const pool = status?.pool;
  const logs = status?.logs || [];
  const workerStatus = status?.status || 'offline';

  const runtime = stats?.startedAt
    ? Math.floor((Date.now() - new Date(stats.startedAt).getTime()) / 1000 / 60)
    : 0;

  return (
    <div className="px-8 pt-6 pb-8 max-w-7xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <h1 className="text-lg font-medium">Scraper Admin</h1>
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${statusColors[workerStatus]}`} />
            <span className="text-sm font-medium capitalize">{workerStatus}</span>
            {runtime > 0 && (
              <span className="text-sm text-muted-foreground">({runtime}m uptime)</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="destructive"
            onClick={() => handleAction('stop')}
            disabled={isActioning || workerStatus !== 'running'}
          >
            Stop Worker
          </Button>
          <Button
            variant="primary"
            onClick={() => handleAction('restart')}
            disabled={isActioning}
          >
            Restart Worker
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-4 gap-4 mb-4">
          <Card className="p-5 rounded-xl">
            <p className="text-sm text-muted-foreground">Companies Processed</p>
            <p className="text-3xl font-semibold mt-1">{stats.companiesProcessed.toLocaleString()}</p>
          </Card>
          <Card className="p-5 rounded-xl">
            <p className="text-sm text-muted-foreground">PSCs Searched</p>
            <p className="text-3xl font-semibold mt-1">{stats.pscsSearched.toLocaleString()}</p>
          </Card>
          <Card className="p-5 rounded-xl">
            <p className="text-sm text-muted-foreground">Prospects Saved</p>
            <p className="text-3xl font-semibold mt-1 text-primary">{stats.prospectsSaved.toLocaleString()}</p>
          </Card>
          <Card className="p-5 rounded-xl">
            <p className="text-sm text-muted-foreground">Errors</p>
            <p className="text-3xl font-semibold mt-1 text-danger-text">{stats.errors.toLocaleString()}</p>
          </Card>
        </div>
      )}

      {/* Second row: more stats */}
      {stats && (
        <div className="grid grid-cols-4 gap-4 mb-4">
          <Card className="p-5 rounded-xl">
            <p className="text-sm text-muted-foreground">PSCs Verified</p>
            <p className="text-3xl font-semibold mt-1">{stats.pscsVerified.toLocaleString()}</p>
          </Card>
          <Card className="p-5 rounded-xl">
            <p className="text-sm text-muted-foreground">Websites Found</p>
            <p className="text-3xl font-semibold mt-1">{stats.websitesFound.toLocaleString()}</p>
          </Card>
          <Card className="p-5 rounded-xl">
            <p className="text-sm text-muted-foreground">Employees Found</p>
            <p className="text-3xl font-semibold mt-1">{stats.employeesFound.toLocaleString()}</p>
          </Card>
          <Card className="p-5 rounded-xl">
            <p className="text-sm text-muted-foreground">Websites Not Found</p>
            <p className="text-3xl font-semibold mt-1 text-muted-foreground">{stats.websitesNotFound.toLocaleString()}</p>
          </Card>
        </div>
      )}

      {/* Proxy IPs */}
      {pool && (
        <div className="mb-8">
          <h2 className="text-sm font-medium mb-3">
            Browser Pool — {pool.total} tabs ({pool.available} available, {pool.waiting} waiting)
          </h2>
          <div className="grid grid-cols-5 gap-3">
            {pool.ips.map((ip) => (
              <Card key={ip.label} className="p-4 rounded-xl min-h-[140px]">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">{ip.label}</p>
                  <p className="text-xs text-muted-foreground">{ip.activeTabs?.length || 0}/{ip.tabs} busy</p>
                </div>
                {ip.activeTabs && ip.activeTabs.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {ip.activeTabs.map((tab, j) => (
                      <div key={j} className="flex items-center gap-2 text-xs">
                        <span className={`font-mono truncate flex-1 ${tab.durationSec > 30 ? 'text-console-danger' : tab.durationSec > 15 ? 'text-caution-solid' : 'text-muted-foreground'}`}>
                          {tab.query.length > 40 ? tab.query.slice(0, 40) + '...' : tab.query}
                        </span>
                        <span className={`shrink-0 font-mono ${tab.durationSec > 30 ? 'text-console-danger font-bold' : 'text-muted-foreground'}`}>
                          {tab.durationSec}s
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Live Logs */}
      <div>
        <h2 className="text-sm font-medium mb-3">Live Logs ({logs.length})</h2>
        <Card className="rounded-xl overflow-hidden bg-console-surface border-console-border">
          <div className="h-[400px] overflow-y-auto p-4 bg-console-surface font-mono text-xs">
            {logs.length === 0 ? (
              <p className="text-console-foreground-muted">No logs yet</p>
            ) : (
              logs.map((log, i) => (
                <div key={i} className="flex gap-3 py-0.5">
                  <span className="text-console-timestamp shrink-0">
                    {new Date(log.timestamp).toLocaleTimeString()}
                  </span>
                  <span className={logTypeColors[log.type] || 'text-console-foreground-dim'}>
                    {log.message}
                  </span>
                </div>
              ))
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
